/**
 * What is being done on a free layer, shared between the page and the panel.
 *
 * Which elements are selected, which text is being typed into, whether the
 * magnets are on, where elements are while a hand moves them, and what was
 * last copied. The page draws all of that and the panel edits it, and the two
 * are different component trees with no ancestor worth threading a prop
 * through, so the state is module-scoped, exactly as the section drag's is.
 *
 * Which *layer* is selected is not here: that is the report's own selection,
 * since a layer is a section. What is here only means anything while a layer
 * is selected, and reads as nothing the moment it is not.
 */

import { computed, readonly, ref, shallowRef } from 'vue'

import type { FreeBox, FreeElement, Guide } from '@/domain/free'

/**
 * Elements under the hand, and where they are at this moment.
 *
 * Several boxes, because a selection moves together; one turn, because only
 * one element is ever turned at a time, by its own handle.
 */
export interface Gesture {
  readonly boxes: ReadonlyMap<string, FreeBox>
  readonly turn: { readonly id: string; readonly angle: number } | null
}

const selectedIds = ref<readonly string[]>([])
const editingId = ref<string | null>(null)
const magnets = ref(true)
const gesture = shallowRef<Gesture | null>(null)
const landed = shallowRef<readonly Guide[]>([])

/**
 * What was last copied, kept for this session only.
 *
 * Not the system clipboard: an element is a structured thing with pictures in
 * it, and pasting one into a mail as a wall of JSON helps nobody. Copying on
 * one page and pasting on another is what this is for.
 */
const clipboard = shallowRef<readonly FreeElement[]>([])

export function useFreeLayer() {
  /** The one element selected on its own, or nothing when none or several are. */
  const selectedId = computed(() => (selectedIds.value.length === 1 ? selectedIds.value[0] : null))

  function isSelected(id: string): boolean {
    return selectedIds.value.includes(id)
  }

  /** Select exactly these, or nothing. Selecting anything else ends the typing. */
  function select(ids: string | readonly string[] | null): void {
    const next = ids === null ? [] : typeof ids === 'string' ? [ids] : [...ids]
    selectedIds.value = next
    if (editingId.value !== null && !next.includes(editingId.value)) editingId.value = null
  }

  /** Add an element to the selection, or take it out: what Shift and a click do. */
  function toggle(id: string): void {
    select(
      isSelected(id) ? selectedIds.value.filter((it) => it !== id) : [...selectedIds.value, id],
    )
  }

  /** Start or stop typing into a text element, which selects it alone as well. */
  function edit(id: string | null): void {
    editingId.value = id
    if (id !== null) selectedIds.value = [id]
  }

  function setMagnets(on: boolean): void {
    magnets.value = on
  }

  /** Where the elements under the hand are now, and the guides they have landed on. */
  function move(next: Gesture | null, guides: readonly Guide[] = []): void {
    gesture.value = next
    landed.value = guides
  }

  function copy(elements: readonly FreeElement[]): void {
    clipboard.value = [...elements]
  }

  return {
    selectedIds: readonly(selectedIds),
    selectedId,
    editingId: readonly(editingId),
    magnets: readonly(magnets),
    gesture: readonly(gesture),
    landed: readonly(landed),
    clipboard: readonly(clipboard),
    isSelected,
    select,
    toggle,
    edit,
    setMagnets,
    move,
    copy,
  }
}
